import React, { Component } from "react";
import { AgGridReact } from "ag-grid-react";
import { ModuleRegistry } from "ag-grid-community";

import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import "bootstrap/dist/css/bootstrap.min.css";
import "../../assets/css/custom.css";
import AgGridLoadingOverlay from "./AgGridLoadingOverlay";

const FILTER_DEBOUNCE_MS = 400;
const DEFAULT_HEIGHT = "calc(100vh - 210px)";

let communityModulesRegistered = false;

function registerAgGridCommunityModules() {
    if (communityModulesRegistered) {
        return;
    }
    communityModulesRegistered = true;

    // AllCommunityModule is exported in ag-grid v33+; v31 ships community features
    // in the main package and must not call registerModules with a partial set.
    const { AllCommunityModule } = require("ag-grid-community");
    if (AllCommunityModule) {
        ModuleRegistry.registerModules([AllCommunityModule]);
    }
}

registerAgGridCommunityModules();

class AgGridComponent extends Component {
    constructor(props) {
        super(props);
        this.gridApi = null;
        this.filterDebounceTimer = null;
    }

    componentDidUpdate(prevProps) {
        this.syncLoadingOverlay(prevProps.isLoading);
    }

    componentWillUnmount() {
        if (this.filterDebounceTimer) {
            clearTimeout(this.filterDebounceTimer);
        }
    }

    syncLoadingOverlay = (prevIsLoading) => {
        if (!this.gridApi || this.props.isLoading === prevIsLoading) {
            return;
        }

        if (this.props.isLoading) {
            this.gridApi.showLoadingOverlay();
        } else {
            this.gridApi.hideOverlay();
        }
    };

    getSortModel = () => {
        if (!this.gridApi) {
            return [];
        }

        return this.gridApi
            .getColumnState()
            .filter((column) => column.sort != null)
            .sort((left, right) => (left.sortIndex || 0) - (right.sortIndex || 0))
            .map((column) => ({
                colId: column.colId,
                sort: column.sort,
            }));
    };

    handleGridReady = (params) => {
        this.gridApi = params.api;

        if (this.props.isLoading) {
            this.gridApi.showLoadingOverlay();
        }
    };

    handleSortChanged = () => {
        const { onSortChange } = this.props;
        if (!this.gridApi || !onSortChange) {
            return;
        }

        onSortChange(this.getSortModel());
    };

    handleFilterChanged = () => {
        const { onFilterChange, serverSideSortFilter = true, filterOnEnterOnly = serverSideSortFilter } = this.props;
        if (!this.gridApi || !onFilterChange) {
            return;
        }

        const notifyParent = () => {
            if (this.gridApi) {
                onFilterChange(this.gridApi.getFilterModel());
            }
        };

        // With filterParams.buttons = ['apply'], ag-Grid only commits floating
        // filter text on Enter (or Apply in the column menu), so fire immediately.
        if (serverSideSortFilter && filterOnEnterOnly) {
            notifyParent();
            return;
        }

        if (this.filterDebounceTimer) {
            clearTimeout(this.filterDebounceTimer);
        }

        this.filterDebounceTimer = setTimeout(notifyParent, FILTER_DEBOUNCE_MS);
    };

    isExternalFilterPresent = () => {
        if (!this.gridApi) {
            return false;
        }

        const filterModel = this.gridApi.getFilterModel();
        return Object.keys(filterModel).length > 0;
    };

    doesExternalFilterPass = () => true;

    render() {
        const {
            columnDefs,
            rowData,
            height = DEFAULT_HEIGHT,
            serverSideSortFilter = true,
            filterOnEnterOnly = serverSideSortFilter,
            fillHeight = false,
        } = this.props;

        const defaultColDef = {
            minWidth: 80,
            sortable: true,
            filter: "agTextColumnFilter",
            floatingFilter: true,
            resizable: true,
        };

        if (serverSideSortFilter) {
            defaultColDef.comparator = () => 0;
        }

        if (serverSideSortFilter && filterOnEnterOnly) {
            // ag-Grid floating filters sync to the parent filter on each keystroke by default.
            // Apply-button mode keeps typed text local until Enter (or Apply in the menu).
            defaultColDef.filterParams = {
                buttons: ["apply"],
            };
        }

        const gridOptions = {
            columnDefs,
            rowData: rowData || [],
            headerHeight: 36,
            floatingFiltersHeight: 34,
            rowHeight: 32,
            animateRows: true,
            suppressHorizontalScroll: false,
            defaultColDef,
            suppressMultiSort: true,
            suppressPaginationPanel: true,
            loadingOverlayComponent: AgGridLoadingOverlay,
            onGridReady: this.handleGridReady,
        };

        if (serverSideSortFilter) {
            gridOptions.isExternalFilterPresent = this.isExternalFilterPresent;
            gridOptions.doesExternalFilterPass = this.doesExternalFilterPass;
            gridOptions.onSortChanged = this.handleSortChanged;
            gridOptions.onFilterChanged = this.handleFilterChanged;
        }

        const gridHeight = fillHeight ? "100%" : height;

        const scrollClassName = fillHeight
            ? "custom-ag-grid-scroll custom-ag-grid-scroll--fill-height"
            : "custom-ag-grid-scroll";

        return (
            <div className={scrollClassName}>
                <div
                    className="custom-ag-grid ag-theme-alpine"
                    style={{ width: "100%", height: gridHeight }}
                >
                    <AgGridReact {...gridOptions} />
                </div>
            </div>
        );
    }
}

export default AgGridComponent;
